import { initCell } from "modules/Core/core-modules/CoreModuleState/utils/cells";
import { compact } from "lodash";
import { CYCLES, getDefaultCycleFlow } from "../consts";
import feed from "./feed";
import { call, put, select } from "redux-saga/effects";
import middleware from "../middleware";
import { onSuccess } from "modules/Core/sub-modules/Dialog/state/cells";
import { createSelector } from "@reduxjs/toolkit";

const refetchCycle = function* (cycleId) {
  const result = yield call(middleware.ops.fetchEntity, { id: cycleId });
  const cycle = result?.entity;
  if (!cycle?.id) return;
  yield put(feed.cells.fetchEntity.onSuccess({ overwrite: true, ...cycle }));
}

const getCycleFlowIds = (cycle) => compact(cycle?.flow).map((activity) => activity.id)

const fetchCurrentCycleCell = initCell(CYCLES, {
  name: "fetchCurrentCycle",
  reducer: (state, { payload }) => {
    state.isLoading = true;
  },
  selectors: {
    activeCycle: createSelector(
      (state) => ({ entities: state.entities, activeCycleId: state.activeCycleId }),
      ({ entities, activeCycleId }) => {
        return entities[activeCycleId]
      }
    ),
    cycle: createSelector(
      (state) => state.entities,
      (entities, id) => entities[id]
    ),
    isLoading: (state) => state.isLoading,
  },
  sagas: {
    latest: function* ({ payload: { boothId } }) {
      const result = yield call(middleware.ops.fetchCurrentcycle, { boothId });
      if (!result.currentCycle.id) throw new Error("Failed to fetch cycle");
      return result.currentCycle;
    },
    onCellSuccess: true
  },
  successCell: {
    reducer: (state, { payload }) => {
      state.isLoading = false;
      state.entities[payload.id] = payload;
      state.activeCycleId = payload.id; //TODO: change if able to focus on single cell differently
    },
  },
});

const addGatewayToActiveCycle = initCell(CYCLES, {
  name: "addGatewayToActiveCycle",
  sagas: {
    latest: function* ({ payload: { gatewayId, orderKey, callback } }) {
      const cycle = yield select(fetchCurrentCycleCell.selectors.activeCycle);

      if (!orderKey) {
        const res = yield call(middleware.ops.addGatewayToCycle, { gatewayId, cycleId: cycle?.id });
        if (!res?.addGatewayToCycle?.id) {
          throw new Error(res?.error || "Failed to add checkpoint as a task");
        }
      } else {
        if (cycle[orderKey])
          throw new Error("Step already exists in this cycle");
        const payload = {};
        payload[orderKey] = gatewayId;
        yield put(
          feed.cells.createEntity.action({ input: payload, id: cycle.id })
        );
      }
      yield put(onSuccess("Checkpoint added as a task"));
      if (cycle?.boothId) {
        yield put(fetchCurrentCycleCell.action({ boothId: cycle.boothId }));
      }
      if (typeof callback === "function") callback();
    },
    onCellSuccess: true
  },
});

export default {
  addGatewayToActiveCycle,
  addGatewayToCycle: initCell(CYCLES, {
    name: "addGatewayToCycle",
    sagas: {
      latest: function* ({ payload: { gatewayId, cycleId, orderKey } }) {
        if (!cycleId)
          return yield put(
            addGatewayToActiveCycle.action({ gatewayId, orderKey })
          );
        const payload = {};
        payload[orderKey] = gatewayId;
        yield put(
          feed.cells.createEntity.action({
            input: { [orderKey]: gatewayId },
            id: cycleId,
          })
        );
        yield put(onSuccess("Step added to cycle"));
      },
    },
  }),
  reorderCycleGateway: initCell(CYCLES, {
    name: "reorderCycleGateway",
    sagas: {
      latest: function* ({ payload: { orderKey, newOrderKey, cycleId } }) {
        const cycle = yield select(feed.cells.fetchEntity.selector(cycleId));
        const payload = {
          [orderKey]: cycle[newOrderKey]?.id || null,
          [newOrderKey]: cycle[orderKey]?.id || null,
        };
        yield put(
          feed.cells.createEntity.action({ input: payload, id: cycleId })
        );
        yield put(feed.cells.fetchEntity.action({ id: cycleId, overwrite: true }))
      },
    },
  }),
  removeGatewayFromActiveCycle: initCell(CYCLES, {
    name: "removeGatewayFromActiveCycle",
    sagas: {
      latest: function* ({ payload: { orderKey, cycleId } }) {
        const payload = {
          [orderKey]: null,
        };
        yield put(
          feed.cells.createEntity.action({ input: payload, id: cycleId })
        );

        yield* refetchCycle(cycleId)
      },
    },
  }),
  upsertActivity: initCell(CYCLES, {
    name: "upsertActivity",
    sagas: {
      latest: function* ({ payload: { input, id } }) {
        const result = yield call(middleware.ops.upsertActivity, { input, id });
        if (!result?.upsertActivity?.id) throw new Error("Failed to save activity");
        if (input?.cycleId) yield* refetchCycle(input.cycleId);
        return result.upsertActivity;
      },
    },
  }),
  stampActivity: initCell(CYCLES, {
    name: "stampActivity",
    sagas: {
      latest: function* ({ payload: { id, key, cycleId } }) {
        const result = yield call(middleware.ops.stampActivity, { id, key });
        if (!result?.stampActivity?.id) throw new Error("Failed to stamp activity");
        if (cycleId) yield* refetchCycle(cycleId);
        return result.stampActivity;
      },
    },
  }),
  setCycleFlow: initCell(CYCLES, {
    name: "setCycleFlow",
    sagas: {
      latest: function* ({ payload: { cycleId, activityIds } }) {
        const result = yield call(middleware.ops.setCycleFlow, { cycleId, activityIds });
        if (!result?.setCycleFlow?.id) throw new Error("Failed to update cycle flow");
        yield* refetchCycle(cycleId);
        return result.setCycleFlow;
      },
    },
  }),
  reorderCycleActivity: initCell(CYCLES, {
    name: "reorderCycleActivity",
    sagas: {
      latest: function* ({ payload: { cycleId, activityId, moveUp } }) {
        const cycle = yield select(feed.cells.fetchEntity.selector(cycleId));
        const flowIds = getCycleFlowIds(cycle);
        const index = flowIds.indexOf(activityId);
        const swapWith = moveUp ? index - 1 : index + 1;
        if (index < 0 || swapWith < 0 || swapWith >= flowIds.length) return;
        [flowIds[index], flowIds[swapWith]] = [flowIds[swapWith], flowIds[index]];
        const result = yield call(middleware.ops.setCycleFlow, { cycleId, activityIds: flowIds });
        if (!result?.setCycleFlow?.id) throw new Error("Failed to reorder activity");
        yield* refetchCycle(cycleId);
        return result.setCycleFlow;
      },
    },
  }),
  removeActivityFromCycle: initCell(CYCLES, {
    name: "removeActivityFromCycle",
    sagas: {
      latest: function* ({ payload: { cycleId, activityId } }) {
        const cycle = yield select(feed.cells.fetchEntity.selector(cycleId));
        const activityIds = getCycleFlowIds(cycle).filter((id) => id !== activityId);
        const result = yield call(middleware.ops.setCycleFlow, { cycleId, activityIds });
        if (!result?.setCycleFlow?.id) throw new Error("Failed to remove activity");
        yield* refetchCycle(cycleId);
        return result.setCycleFlow;
      },
    },
  }),
  assignActivityToCycle: initCell(CYCLES, {
    name: "assignActivityToCycle",
    sagas: {
      latest: function* ({ payload: { cycleId, placeholderId, activityId } }) {
        const result = yield call(middleware.ops.assignActivityToCycle, {
          cycleId,
          placeholderId,
          activityId,
        });
        if (!result?.assignActivityToCycle?.id) throw new Error("Failed to assign activity");
        yield* refetchCycle(cycleId);
        return result.assignActivityToCycle;
      },
    },
  }),
  focusCycle: initCell(CYCLES, {
    name: "focusCycle",
    sagas: {
      latest: function* ({ payload: { id } }) {
        const result = yield call(middleware.ops.focusCycle, { id });
        if (!result.focusCycle.id) {
          throw new Error("Failed to focus cycle");
        }
        return result.focusCycle.id;
      },
    },
  }),
  fetchCurrentCycle: fetchCurrentCycleCell,
  createCycle: initCell(CYCLES, {
    name: "createCycle",
    sagas: {
      latest: function* ({ payload: { input } }) {
        const result = yield call(middleware.ops.create, {
          input: {
            ...input,
            flow: input?.flow ?? getDefaultCycleFlow(),
          },
        });
        if (!result?.upsertCycle?.id) throw new Error("Failed to create cycle");
        return result.upsertCycle;
      },
    },
  }),
  completeCycle: initCell(CYCLES, {
    name: "completeCycle",
    sagas: {
      latest: function* ({ payload: { id } }) {
        const result = yield call(middleware.ops.completeCycle, { id });
        if (!result.completeCycle.id) throw new Error("Failed to complete cycle");
        return result.completeCycle.id;
      },
    },
  }),
};
